/* eslint-disable import/no-duplicates */
import { format, isAfter, isValid, parse, startOfDay, subDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';
import formatRelative from 'date-fns/formatRelative';
import {
  de,
  enCA,
  enGB,
  enUS,
  es,
  et,
  fi,
  fr,
  frCA,
  ja,
  ms,
  nl,
  pt,
  uk,
} from 'date-fns/locale';
import i18n from 'i18next';
/* eslint-enable import/no-duplicates */

import { PERIODS } from 'Consts/defintions';
import type { Period } from 'Consts/types';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const localizedHourFormatString = () => {
  const language = i18n.language;
  return localeFallback(language)?.formatLong?.time()?.charAt(0) === 'h'
    ? 'h aaa'
    : 'HH';
};

const localizedDateAndMonthNoYearFormatString = () => {
  const language = i18n.language;
  return localeFallback(language)
    ?.formatLong?.date({ width: 'medium' })
    .replace(', y', '')
    .replace(' y', '');
};

const localizedHoursMinutesFormatString = () => {
  const language = i18n.language;
  return localeFallback(language)?.formatLong?.time()?.charAt(0) === 'h'
    ? 'h:mm a'
    : 'HH:mm';
};

const localizedHoursMinutesDaysFormatString = () => {
  const language = i18n.language;
  return localeFallback(language)?.formatLong?.time()?.charAt(0) === 'h'
    ? 'h:mm a, dddd MMM D'
    : 'HH:mm, dddd MMM D';
};

export const formatGraphDateLabel = (date: Date, period: Period) => {
  const language = i18n.language;
  const labelFormat =
    period === PERIODS.day
      ? localizedHourFormatString()
      : localizedDateAndMonthNoYearFormatString();

  return format(date, labelFormat, {
    locale: localeFallback(language),
  });
};

export const formatHour = (date: Date, timezone: string) => {
  const language = i18n.language;
  return format(getZonedDateObject(date, timezone), 'p', {
    locale: localeFallback(language),
  });
};

export const localeFallback = (language: string) => {
  const normalizedLanguage = language.replace('_', '-');

  return (
    LOCALES[normalizedLanguage] ||
    LOCALES[normalizedLanguage.split('-')[0]] ||
    LOCALES['en-US']
  );
};

export const LOCALES: Record<string, Locale> = {
  'en-US': enUS,
  'en-GB': enGB,
  'en-CA': enCA,
  'en-SG': enUS,
  en: enUS,
  fr: fr,
  fi: fi,
  'fr-FR': fr,
  'fr-CA': frCA,
  ja: ja,
  pt: pt,
  'pt-PT': pt,
  es: es,
  de: de,
  'es-ES': es,
  'es-US': es,
  et: et,
  'et-ET': et,
  nl: nl,
  'nl-NL': nl,
  ms: ms,
  uk: uk,
};

export const formatRelativeToNowDAYJS = (date: Date, timezone: string) => {
  if (date.getTime() === 0) {
    return i18n.t('common.moreThan30Days');
  }
  const language = i18n.language;

  const now = new Date(dayjs().tz(timezone).toISOString());

  return formatRelative(date, now, {
    locale: localeFallback(language),
    weekStartsOn: 1,
  });
};

export const formatRelativeToNowDATEFNS = (date: Date, timezone: string) => {
  if (date.getTime() === 0) {
    return i18n.t('common.moreThan30Days');
  }
  const dataDate = getZonedDateObject(date, timezone);
  const now = getZonedNowDateObject(timezone);
  const language = i18n.language;
  return formatRelative(dataDate, now, {
    locale: localeFallback(language),
    weekStartsOn: 1,
  });
};

export const formatHours = (date: Date) => {
  const language = i18n.language;
  return format(date, localizedHourFormatString(), {
    locale: localeFallback(language),
  });
};

export const formatHoursMinutes = (date: string, timezone: string) => {
  return dayjs(date).tz(timezone).format(localizedHoursMinutesFormatString());
};

export const formatHoursMinutesDays = (date: string, timezone: string) => {
  return dayjs(date)
    .tz(timezone)
    .format(localizedHoursMinutesDaysFormatString());
};

export const formatDistanceFromNow = (
  date: Date,
  addSuffix: boolean = false
) => {
  const thirtyDaysAgo = startOfDay(subDays(new Date(), 30));
  const lastSeenWithinLast30Days = isAfter(date, thirtyDaysAgo);

  if (!lastSeenWithinLast30Days) {
    return i18n.t('common.moreThan30Days');
  }
  const language = i18n.language;
  return formatDistanceToNowStrict(date, {
    locale: localeFallback(language),
    addSuffix,
  });
};

export const formatMonthDay = (date: Date) => {
  const language = i18n.language;

  return format(new Date(date), localizedDateAndMonthNoYearFormatString(), {
    locale: localeFallback(language),
  });
};

export const formatWeekday = (date: Date) => {
  const language = i18n.language;

  return format(date, 'EEEE', {
    locale: localeFallback(language),
  });
};

export const parseIsoDateOnly = (isoDate: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);

  if (!match) {
    return null;
  }

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

export const formatLocalizedWeekday = (
  visitDate: string,
  visitDayOfWeek: string
): string => {
  const fromVisitDate = parseIsoDateOnly(visitDate);

  if (fromVisitDate) {
    return formatWeekday(fromVisitDate);
  }

  if (!visitDayOfWeek) {
    return visitDayOfWeek;
  }

  const fromDayName = parse(visitDayOfWeek, 'EEEE', new Date(), {
    locale: enUS,
  });

  return isValid(fromDayName) ? formatWeekday(fromDayName) : visitDayOfWeek;
};

export const formatCsvFilenameDatestamp = (
  timezone: string,
  now: Date = new Date()
): string => {
  const tz = timezone || 'UTC';

  try {
    return dayjs(now).tz(tz).format('YYYYMMDD');
  } catch {
    return dayjs(now).utc().format('YYYYMMDD');
  }
};

export const formatSecondsToHoursAndMinutes = (sec: number) => {
  const unitSeconds = i18n.t('common.seconds');
  const unitMinute = i18n.t('common.min');
  const unitHour = i18n.t('common.hr');
  if (sec < 60) {
    return `${sec.toFixed(0)} ${unitSeconds}`;
  }
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor(hours >= 1 ? (sec % 3600) / 60 : sec / 60);
  // Unable to use formatDistanceToNowStrict due to the short name of the hours/minutes i.e. 'min' and 'hr'
  const hourText = `${hours}${unitHour}`;
  const minutesText = `${minutes}${unitMinute}`;

  return hours > 0 ? `${hourText} ${minutesText}` : minutesText;
};

export const formatMinutesToHours = (min: number) => {
  // TODO check this
  return formatSecondsToHoursAndMinutes(min * 60);
};

export const getZonedDateObject = (date: string | Date, timezone: string) => {
  return utcToZonedTime(new Date(date), timezone);
};

export const getZonedHours = (date: string | Date, timezone: string) => {
  return utcToZonedTime(new Date(date), timezone).getHours();
};

export const getZonedDate = (date: string | Date, timezone: string) => {
  return utcToZonedTime(new Date(date), timezone).getDate();
};

export const getZonedNowDateObject = (timezone: string) => {
  return utcToZonedTime(new Date(), timezone);
};

export const getZonedNowHours = (timezone: string) => {
  return utcToZonedTime(new Date(), timezone).getHours();
};
export const isTodayFromAppTimeDate = (appTimeDate: string | undefined) => {
  const todayStartDate = new Date().toISOString().substring(0, 10);
  return todayStartDate === appTimeDate;
};
